import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { triggerCatalogSync, type CatalogSyncResult } from '@/api/console';
import { Button, Callout, Modal } from '@/components/ui';

interface CatalogSyncPreviewModalProps {
    open: boolean;
    onClose: () => void;
}

function ResultRow({ result }: { result: CatalogSyncResult }) {
    const { t } = useTranslation();
    if (result.status === 'skipped') {
        return (
            <li>
                <code>{result.application}</code>{' '}
                {t('console.apps.syncPreviewModal.resultSkipped', {
                    reason: result.reason ?? t('console.apps.syncPreviewModal.defaultSyncedReason'),
                })}
            </li>
        );
    }
    // status === 'preview' — the only status a dry run ever returns.
    const wouldAdd = result.would_add?.length ?? 0;
    const wouldDeactivate = result.would_deactivate?.length ?? 0;
    const wouldPause = result.would_pause ?? [];
    return (
        <li>
            <code>{result.application}</code>{' '}
            {t('console.apps.syncPreviewModal.resultPreview', { wouldAdd, wouldDeactivate })}
            {wouldPause.length > 0 && (
                <>
                    ,{' '}
                    <strong>
                        {t('console.apps.syncPreviewModal.resultPauseWarning', {
                            count: wouldPause.length,
                            items: wouldPause.join(', '),
                        })}
                    </strong>
                </>
            )}
        </li>
    );
}

/**
 * EAA-1817: page-level "xem trước đồng bộ danh mục" — a dry run of
 * `POST /role-map/sync?dry_run=true`. This is the SAME endpoint the
 * `/console/role-map` screen's live "Đồng bộ lại" button calls (see
 * `useRoleMapWrites.ts`), just with `dryRun: true`, and it syncs every
 * application's catalog in one call — there is no per-app variant — so this
 * lives at the page level rather than per row. Gated by `console.rolemap.sync`
 * to match that existing button's capability rather than `console.apps.*`,
 * since it is genuinely the same write-adjacent capability the server checks.
 *
 * A dry run performs NO writes (`RoleMapService`'s dry-run path never calls
 * the mutating catalog-sync branch), so this has no destructive confirm —
 * the whole point is letting an operator see `would_pause` BEFORE running a
 * real sync.
 */
export function CatalogSyncPreviewModal({ open, onClose }: CatalogSyncPreviewModalProps) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();

    const previewMutation = useMutation({
        mutationFn: () =>
            triggerCatalogSync(config.apiBase, accessToken as string, { dryRun: true }),
    });

    function handleClose() {
        onClose();
    }

    function handleRun() {
        previewMutation.mutate();
    }

    return (
        <Modal
            open={open}
            onCancel={handleClose}
            title={t('console.apps.syncPreviewModal.title')}
            width={560}
            footer={
                <>
                    {/* EAA-1817: raw <button>s → kit Button (compliance sweep). */}
                    <Button onClick={handleClose}>{t('common.close')}</Button>
                    <Button type="primary" onClick={handleRun} disabled={previewMutation.isPending}>
                        {previewMutation.isPending
                            ? t('console.apps.syncPreviewModal.runningLabel')
                            : t('console.apps.syncPreviewModal.runButton')}
                    </Button>
                </>
            }
        >
            <p>{t('console.apps.syncPreviewModal.description')}</p>
            {previewMutation.isError && (
                <p className="portal-admin-error">
                    {t('console.apps.syncPreviewModal.errorMessage')}
                </p>
            )}
            {previewMutation.data && (
                <>
                    {previewMutation.data.would_pause_count > 0 && (
                        <Callout
                            tone="warning"
                            label={t('console.apps.syncPreviewModal.pauseWarningLabel')}
                        >
                            {t('console.apps.syncPreviewModal.pauseWarningBody', {
                                count: previewMutation.data.would_pause_count,
                            })}
                        </Callout>
                    )}
                    <ul className="portal-console-sync-results">
                        {previewMutation.data.results.map((result) => (
                            <ResultRow key={result.application} result={result} />
                        ))}
                    </ul>
                </>
            )}
        </Modal>
    );
}
